import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { ArFormField } from "./FormField";
import {
    FieldArrayMethodProps,
    FieldArrayWithId,
    FieldValues,
    ArrayPath,
    Path,
    useFormContext,
    PathValue,
} from "react-hook-form";
import { TermsAndConditionsComboBox } from "../combo-box";
import { useEffect, useState } from "react";
import { TermsAndCondition } from "@/v2/types";

type FormWithTerms = FieldValues & {
    terms_and_conditions: Array<{ value: string }>;
};

interface TermsAndConditionFormFieldProps<T extends FormWithTerms> {
    label: string;
    placeholder: string;
    name: ArrayPath<T>;
    append: (
        value:
            | {
                  value: string;
              }
            | {
                  value: string;
              }[],
        options?: FieldArrayMethodProps,
    ) => void;
    remove: (index?: number | number[]) => void;
    termsFields: FieldArrayWithId<T, ArrayPath<T>, "id">[];
}

export default function TermsAndConditionFormField<T extends FormWithTerms>({
    append,
    remove,
    termsFields,
    label,
    name,
    placeholder,
}: TermsAndConditionFormFieldProps<T>) {
    const { setValue } = useFormContext<T>();
    const [selectedTemplate, setSelectedTemplate] =
        useState<TermsAndCondition | null>(null);

    useEffect(() => {
        if (selectedTemplate) {
            const transformedData = selectedTemplate.terms_and_conditions.map(
                (val) => ({
                    value: val,
                }),
            );

            setValue(name as Path<T>, transformedData as PathValue<T, Path<T>>);
        }
    }, [selectedTemplate]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Terms and Conditions</h3>
                <div className="flex h-full items-end gap-4">
                    <TermsAndConditionsComboBox
                        name={name as Path<T>}
                        setSelected={setSelectedTemplate}
                    />
                    <Button
                        type="button"
                        variant="default"
                        onClick={() => append({ value: "" })}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Term
                    </Button>
                </div>
            </div>
            {termsFields.map((field, index: number) => (
                <div key={field.id} className="flex gap-2">
                    <div className="flex-1">
                        <ArFormField<T>
                            label={`${label} ${index + 1}`}
                            name={`${name}.${index}.value` as Path<T>}
                            placeholder={placeholder}
                        />
                    </div>
                    {termsFields.length > 1 && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="mt-8"
                        >
                            <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );
}
